import { useState, useEffect, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import { Play, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { videos, youtubeThumbAt, YT_THUMB_TIERS, driveThumb } from "../data/videos";

const N = videos.length;
// Clone the first slide at the end for a seamless forward loop.
const slides = [...videos, videos[0]];

const DWELL = 2000; // each slide stays visible ~2s
const SWEEP = 0.85; // seconds for the left sweep
const EASE = [0.83, 0, 0.17, 1];

function Slide({ video, onOpen, clone, priority }) {
  const isDrive = video.provider === "drive";
  // Start on `hqdefault` — it's the fastest-serving tier that YouTube
  // guarantees for every public video. Upgrade to maxres only on success.
  const [tier, setTier] = useState(2); // index into YT_THUMB_TIERS: hqdefault
  const src = isDrive
    ? video.poster || driveThumb(video.driveId)
    : youtubeThumbAt(video.youtubeId, YT_THUMB_TIERS[tier]);

  const handleError = () => {
    if (!isDrive && tier < YT_THUMB_TIERS.length - 1) {
      setTier((t) => t + 1);
    }
  };
  return (
    <div className="w-full shrink-0 px-1 md:px-2">
      <button
        data-testid={`branded-card-${video.id}${clone ? "-clone" : ""}`}
        onClick={() => onOpen(video)}
        className="group relative block aspect-video w-full overflow-hidden rounded-xl text-left"
      >
        <div className="absolute inset-0 z-10 bg-black/40 transition-colors duration-700 group-hover:bg-black/20" />
        <img
          key={src}
          src={src}
          alt={video.title}
          loading={priority ? "eager" : "lazy"}
          decoding="async"
          onError={handleError}
          referrerPolicy="no-referrer"
          className="h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-105"
        />

        {video.badge && (
          <span className="absolute left-4 top-4 z-20 rounded-full border border-[#CBFF3A]/60 bg-black/60 px-3 py-1 font-hud text-[10px] font-700 uppercase tracking-widest text-[#CBFF3A] backdrop-blur-sm">
            {video.badge}
          </span>
        )}

        {/* Play */}
        <div className="absolute inset-0 z-20 flex items-center justify-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full glass-panel transition-transform duration-500 group-hover:scale-110 md:h-20 md:w-20">
            <Play className="h-6 w-6 translate-x-[2px] fill-[#CBFF3A] text-[#CBFF3A] md:h-7 md:w-7" />
          </div>
        </div>

        {/* Caption */}
        <div className="absolute bottom-0 left-0 z-20 w-full p-6 md:p-10">
          <span className="mb-3 inline-block bg-[#CBFF3A] px-3 py-1 font-hud text-[10px] font-700 uppercase tracking-widest text-black">
            {video.role}
          </span>
          <h3 className="font-display text-2xl font-600 leading-tight tracking-tight text-white md:text-4xl">
            {video.title}
          </h3>
          <p className="mt-1 font-body text-sm text-white/70">
            {video.client} · {video.duration}
          </p>
        </div>
      </button>
    </div>
  );
}

export default function BrandedBento({ onOpen }) {
  const [index, setIndex] = useState(0);
  const [animate, setAnimate] = useState(true);
  const [paused, setPaused] = useState(false);
  const [resetKey, setResetKey] = useState(0);
  const timer = useRef(null);

  const open = (v) => onOpen({ ...v, kind: "video" });

  const advance = useCallback(() => {
    setIndex((i) => {
      // At or past the clone — bail out of the updater and schedule a clean
      // snap-back on the next frame to avoid overshooting into empty space.
      if (i >= N) {
        return i; // keep index put; the effect below will handle the wrap
      }
      return i + 1;
    });
    setAnimate(true);
  }, []);

  // Clean, idempotent wrap: whenever we land on the clone (N), snap to 0 on
  // the next frame with animation disabled, then re-enable animation.
  useEffect(() => {
    if (index !== N) return undefined;
    const raf = requestAnimationFrame(() => {
      setAnimate(false);
      setIndex(0);
      // Re-enable animation on the frame after the snap so the next advance sweeps.
      requestAnimationFrame(() => setAnimate(true));
    });
    return () => cancelAnimationFrame(raf);
  }, [index]);

  useEffect(() => {
    if (paused) return undefined;
    timer.current = setInterval(advance, DWELL + SWEEP * 1000);
    return () => clearInterval(timer.current);
  }, [advance, paused, resetKey]);

  // handleComplete acts as a safety net — if auto-advance stops happening while
  // we're parked on the clone (e.g. tab was backgrounded), snap back once the
  // sweep settles so the next click/press starts from a clean position.
  const handleComplete = () => {
    if (index === N) {
      setAnimate(false);
      setIndex(0);
    }
  };

  const goTo = (i) => {
    setResetKey((k) => k + 1);
    setAnimate(true);
    setIndex(i);
  };

  const next = () => {
    setResetKey((k) => k + 1);
    advance();
  };

  const prev = () => {
    setResetKey((k) => k + 1);
    if (index <= 0) {
      // Jump instantly onto the clone (visually identical to first), then step back.
      setAnimate(false);
      setIndex(N);
      requestAnimationFrame(() => {
        setAnimate(true);
        setIndex(N - 1);
      });
    } else {
      setAnimate(true);
      setIndex(index - 1);
    }
  };

  const activeDot = index % N;

  return (
    <section
      id="work"
      data-testid="branded-bento"
      className="bg-[#0d0d0f] py-24 md:py-32"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-16">
        <div className="mb-10 flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <div>
            <h2 className="font-display text-4xl font-600 tracking-tight text-[#ECEAE3] md:text-5xl">
              Selected Work
            </h2>
            <p className="mt-3 font-body text-white/55">
              Films, brand campaigns & live productions — produced, edited &
              directed.
            </p>
          </div>
          <a
            href="#social"
            data-testid="view-all-projects"
            className="group flex items-center gap-2 font-hud text-[11px] uppercase tracking-[0.2em] text-[#CBFF3A] transition-colors hover:text-white"
          >
            View Work
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </a>
        </div>

        {/* Auto-sweeping horizontal carousel */}
        <div
          data-testid="work-carousel"
          className="relative overflow-hidden"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <motion.div
            className="flex"
            animate={{ x: `-${index * 100}%` }}
            transition={animate ? { duration: SWEEP, ease: EASE } : { duration: 0 }}
            onAnimationComplete={handleComplete}
          >
            {slides.map((v, i) => (
              <Slide
                key={`${v.id}-${i}`}
                video={v}
                onOpen={open}
                clone={i === slides.length - 1}
                priority={Math.abs(i - index) <= 1 || i === slides.length - 1}
              />
            ))}
          </motion.div>

          {/* Prev / Next arrows */}
          <button
            data-testid="work-prev"
            onClick={prev}
            aria-label="Previous project"
            className="absolute left-3 top-1/2 z-30 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full glass-panel text-white transition-colors duration-300 hover:text-[#CBFF3A] md:left-5 md:h-14 md:w-14"
          >
            <ChevronLeft className="h-5 w-5 md:h-6 md:w-6" />
          </button>
          <button
            data-testid="work-next"
            onClick={next}
            aria-label="Next project"
            className="absolute right-3 top-1/2 z-30 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full glass-panel text-white transition-colors duration-300 hover:text-[#CBFF3A] md:right-5 md:h-14 md:w-14"
          >
            <ChevronRight className="h-5 w-5 md:h-6 md:w-6" />
          </button>
        </div>

        {/* Progress + dots */}
        <div className="mt-6 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {videos.map((v, i) => (
              <button
                key={v.id}
                data-testid={`work-dot-${i}`}
                onClick={() => goTo(i)}
                aria-label={`Go to ${v.title}`}
                className={`h-1 rounded-full transition-all duration-500 ${
                  i === activeDot
                    ? "w-10 bg-[#CBFF3A]"
                    : "w-4 bg-white/25 hover:bg-white/50"
                }`}
              />
            ))}
          </div>
          <span data-testid="work-counter" className="font-hud text-[11px] uppercase tracking-[0.2em] text-white/40">
            {String(activeDot + 1).padStart(2, "0")} /{" "}
            {String(N).padStart(2, "0")}
          </span>
        </div>
      </div>
    </section>
  );
}
